import { Component, OnDestroy, OnInit, ViewChild, ElementRef, HostListener, SecurityContext, ChangeDetectorRef } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { combineLatest, Subject } from 'rxjs';
import { ApiService } from 'app/shared/services/api.service';
import { StatoSessioneService } from 'app/shared/services/stato-sessione.service';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '../../../shared/auth/auth.service';
import { switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { TestComponent } from '../test/test.component';
import { ProgettiService } from '../../../shared/services/progetti.service';
import { SpinnerService } from '../../../shared/services/spinner.service';
import Swal from 'sweetalert2';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { TokenCount } from '../../../shared/templateGen/templateInterfaces';
import { ModelPricingService, TokenCostCalculation } from '../../../shared/services/model-pricing.service';
import { DownloaderComponent } from '../test/downloader/downloader.component';
import { SessionPersistenceService } from '../session-manager/session-persistence.service';
import { InferenceSettingsModalComponent } from './inference-settings-modal/inference-settings-modal.component';
import { ToastrService } from 'ngx-toastr';
import { StreamProgressEvent } from '../../../shared/interfaces/fileUploadInterfaces';

export interface SupportFile extends File {
  purpose?: string;
  content?: string;
  fileType?: 'document' | 'image'; // property to distinguish between document and image files
}

export interface TokenCostHistory {
  previousTokenCount: TokenCount;
  previousTokenCost: TokenCostCalculation;
}

@Component({
  selector: 'tool-story',
  templateUrl: './story.component.html',
  styleUrls: ['./story.component.scss'],
  animations: [
    trigger('slideIn', [
      state('hidden', style({
        opacity: 0,
        transform: 'translateY(-20px)'
      })),
      state('visible', style({
        opacity: 1,
        transform: 'translateY(0)'
      })),
      transition('hidden => visible', [
        animate('300ms ease-out')
      ]),
      transition('visible => hidden', [
        animate('200ms ease-in')
      ])
    ])
  ]
})
export class StoryComponent implements OnInit, OnDestroy {
  @ViewChild('fileInputDocx') fileInputDocx: ElementRef<HTMLInputElement>;
  @ViewChild(TestComponent) testComponent: TestComponent;
  @ViewChild('sessionNameInput') sessionNameInput: ElementRef<HTMLInputElement>;

  // Existing variables
  tokenCount: TokenCount = {
    prompt_tokens: 0,
    completion_tokens: 0,
    total_tokens: 0
  };
  tokenCost: TokenCostCalculation = {
    inputCost: 0,
    outputCost: 0,
    totalCost: 0
  };

  tokenHistory: TokenCostHistory = {
    previousTokenCount: {
      prompt_tokens: 0,
      completion_tokens: 0,
      total_tokens: 0
    },
    previousTokenCost: {
      inputCost: 0,
      outputCost: 0,
      totalCost: 0
    }
  };
  isLoading: boolean = false;
  // Context for session chat
  requirementsAnalysis: any;
  macroScenariosUI: any[] = [];
  selectedMicroScenario: any;
  microScenarioSpecifications: any;

  ruolo: string;
  isAgenticMode: boolean;
  models: string[];
  templates: any[];
  templateNames: any[];
  testStyle: string;
  selectedTemplate: string;
  refinedStoryString: string;
  fileName: string;
  projectLanguage: string;
  additionalNotes: string;
  projectId: string;
  timing = false;
  elapsedTime = 0;
  private destroy$: Subject<void> = new Subject<void>();
  isSticky = false;
  stickyThreshold = 150;
  isQAnalystOrManager = false;
  hasSubtable = false;
  topN = 10;
  supportFiles: SupportFile[] = [];  // property to track active tab
  activeTab: 'story' | 'testing' = 'story';

  // Session name editing properties
  currentSessionName: string = '';
  currentSessionDisplayName: string = '';
  isEditingSessionName: boolean = false;
  editingSessionName: string = '';

  @HostListener('window:scroll', ['$event'])
  onScroll() {
    this.isSticky = window.scrollY > this.stickyThreshold;
  }

  constructor(
    private modalService: NgbModal,
    private route: ActivatedRoute,
    private apiService: ApiService,
    protected sessioneService: StatoSessioneService,
    private router: Router,
    private authService: AuthService,
    private changeDetectorRef: ChangeDetectorRef,
    private progettiService: ProgettiService,
    private spinnerService: SpinnerService,
    private modelPricingService: ModelPricingService,
    private downloader: DownloaderComponent,
    private sessionPersistenceService: SessionPersistenceService,
    private toastr: ToastrService
  ) { }

  ngOnInit(): void {
    this.route.params.pipe(
      takeUntil(this.destroy$)
    ).subscribe(params => {
      this.projectId = params['project_id'];
      const sessionId = params['session_id'];

      // 1. Fetch Templates
      this.apiService.getTemplates(this.projectId).subscribe({
        next: (templates) => {
          this.sessioneService.updateTemplates(templates);
          if (this.selectedTemplate) {
            this.checkForSubtable();
          }
        },
        error: (err) => console.error('Failed to load templates', err)
      });

      // 2. Load Session Logic with Loading State
      if (sessionId && this.sessionPersistenceService.getCurrentSessionId() !== sessionId) {
        console.log(`[StoryComponent] Reloading session...`);

        // START LOADING
        this.isLoading = true;
        this.spinnerService.show();

        this.sessionPersistenceService.loadSession(sessionId).subscribe({
          next: () => {
            console.log('[StoryComponent] Session loaded.');
            // STOP LOADING
            this.isLoading = false;
            this.spinnerService.hide();
          },
          error: (err) => {
            console.error('[StoryComponent] Failed to load.', err);
            // STOP LOADING ON ERROR
            this.isLoading = false;
            this.spinnerService.hide();
            this.toastr.error('Failed to load session data.');
            this.router.navigate(['/projects/sessions', this.projectId]);
          }
        });
      }
    });

    this.subscribeToSessionService();

    // Check if a session was loaded from API
    this.route.queryParams.pipe(
      takeUntil(this.destroy$)
    ).subscribe(params => {
      if (params['sessionLoaded'] === 'true') {
        console.log('DEBUG: Session loaded from API detected in StoryComponent');

        // Force update the UI with a slight delay to ensure data is loaded
        setTimeout(() => {
          if (params['template']) {
            const templateName = params['template'];
            if (this.templateNames && this.templateNames.includes(templateName)) {
              this.selectedTemplate = templateName;
              this.sessioneService.updateSelectedTemplate(templateName);
              this.checkForSubtable();
            }
          }
          this.changeDetectorRef.detectChanges();
        }, 300);
      }
    });

    this.isQAnalystOrManager = this.ruolo === 'Admin' || this.ruolo === 'Manager' || this.ruolo === 'QA Analyst';

    // subscription to support files
    this.sessioneService.supportFiles$
      .pipe(takeUntil(this.destroy$))
      .subscribe(files => {
        this.supportFiles = files;
      });

    combineLatest([
      this.sessioneService.tokenUsage$,
      this.sessioneService.inferenceParams$ // <-- USA LA NUOVA FONTE DI VERITÀ
    ]).pipe(
      takeUntil(this.destroy$)
    ).subscribe(([tokenCount, inferenceParams]) => { // <-- USA LA NUOVA FONTE
      // Estraiamo il nome del modello dall'oggetto inferenceParams
      const currentModelName = inferenceParams.model;

      if (tokenCount && currentModelName) {
        // Store previous values before updating
        this.tokenHistory.previousTokenCount = { ...this.tokenCount };
        this.tokenHistory.previousTokenCost = { ...this.tokenCost };

        // Update current values
        this.tokenCount = tokenCount;
        // Passiamo il nome del modello estratto al servizio di pricing
        this.tokenCost = this.modelPricingService.calculateCost(currentModelName, tokenCount);
      }
    });
  }

  openSupportFileUpload(fileType: 'document' | 'image') {
    const input = document.createElement('input');
    input.type = 'file';

    // Set accepted file types based on fileType parameter
    if (fileType === 'document') {
      input.accept = '.txt,.docx,.pdf,.md,.html,.csv,.json';
    } else if (fileType === 'image') {
      input.accept = '.jpg,.jpeg,.png,.gif,.bmp';
    }

    input.onchange = (e: any) => {
      if (!e.target.files || !e.target.files[0]) { return; }

      const file = e.target.files[0];

      // Check file extension based on type
      const fileExtension = file.name.toLowerCase().split('.').pop();
      let allowedExtensions: string[] = [];

      if (fileType === 'document') {
        allowedExtensions = ['txt', 'docx', 'pdf', 'md', 'html', 'csv', 'json'];
      } else if (fileType === 'image') {
        allowedExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp'];
      }

      if (!fileExtension || !allowedExtensions.includes(fileExtension)) {
        alert(`Only ${allowedExtensions.join(', ')} files are supported for this type`);
        return;
      }

      // Create a new object with our custom properties
      const supportFile = {
        ...file,
        purpose: '',
        fileType: fileType,
        lastModified: file.lastModified,
        name: file.name,
        size: file.size,
        type: file.type,
        arrayBuffer: () => file.arrayBuffer(),
        slice: (start?: number, end?: number, contentType?: string) => file.slice(start, end, contentType),
        stream: () => file.stream(),
        text: () => file.text()
      } as SupportFile;

      const isPdf = fileExtension === 'pdf';

      if (isPdf) {
        this._uploadSupportFileWithVision(file, supportFile);
      } else {
        this._uploadSupportFileStandard(file, supportFile);
      }
    };

    input.click();
  }

  /**
   * Standard upload (non-vision): same as original flow.
   */
  private _uploadSupportFileStandard(file: File, supportFile: SupportFile) {
    this.spinnerService.show();
    this.apiService.uploadFile(file, false).subscribe(
      (response: { filename: string, contents: string | string[] }) => {
        this._appendUploadedContentToStory(supportFile, response.contents);
        this.spinnerService.hide();
      },
      (error: any) => {
        console.error('There was an error uploading the support file!', error);
        const updatedFiles = [...this.supportFiles, supportFile];
        this.sessioneService.updateSupportFiles(updatedFiles);
        this.spinnerService.hide();
      }
    );
  }

  /**
   * Vision upload with streaming progress (mirrors KB flow).
   */
  private _uploadSupportFileWithVision(file: File, supportFile: SupportFile) {
    this.spinnerService.show();
    let finalData: any = null;
    let progressToastId: number | null = null;

    this.apiService.uploadFileStream(file, true).subscribe({
      next: (event: StreamProgressEvent) => {
        if (event.type === 'progress' && event.phase === 'vision') {
          const msg = `Analisi visiva pagina ${event.current}/${event.total}...`;
          if (progressToastId !== null) {
            this.toastr.clear(progressToastId);
          }
          const toast = this.toastr.info(msg, 'Vision PDF', {
            disableTimeOut: true,
            closeButton: false,
            tapToDismiss: false
          });
          progressToastId = toast.toastId;
        } else if (event.type === 'progress' && event.phase === 'saving') {
          if (progressToastId !== null) {
            this.toastr.clear(progressToastId);
          }
          const toast = this.toastr.info('Salvataggio in corso...', 'Vision PDF', {
            disableTimeOut: true,
            closeButton: false,
            tapToDismiss: false
          });
          progressToastId = toast.toastId;
        } else if (event.type === 'complete') {
          finalData = event.data;
        } else if (event.type === 'error') {
          console.error('Vision upload error:', event.detail);
        }
      },
      error: (err) => {
        console.error('There was an error uploading the support file with vision!', err);
        if (progressToastId !== null) { this.toastr.clear(progressToastId); }
        const updatedFiles = [...this.supportFiles, supportFile];
        this.sessioneService.updateSupportFiles(updatedFiles);
        this.spinnerService.hide();
      },
      complete: () => {
        if (progressToastId !== null) { this.toastr.clear(progressToastId); }
        if (finalData) {
          this._appendUploadedContentToStory(supportFile, finalData.contents);
        }
        this.spinnerService.hide();
      }
    });
  }

  /**
   * Shared post-processing: append extracted content to story string.
   */
  private _appendUploadedContentToStory(supportFile: SupportFile, contents: string | string[]) {
    const fileContent = Array.isArray(contents) ? contents.join('') : contents;

    const fileInfoForStory = `'${supportFile.name}':\n${fileContent}`;
    const currentStory = this.refinedStoryString || '';
    const newStory = currentStory ? `${currentStory}\n\n${fileInfoForStory}` : fileInfoForStory;

    this.sessioneService.updateRefinedStoryString(newStory);

    const supportFileWithContent = { ...supportFile, content: fileContent } as SupportFile;
    const updatedFiles = [...this.supportFiles, supportFileWithContent];
    this.sessioneService.updateSupportFiles(updatedFiles);
    this.sessioneService.updateSupportFileContent(supportFile.name, fileContent);
  }

  removeSupportFile(index: number) {
    const fileToRemove = this.supportFiles[index];
    if (fileToRemove) {
      this.sessioneService.removeSupportFileByName(fileToRemove.name);
    }
  }

  private subscribeToSessionService(): void {
    this.sessioneService.projectLanguage$.pipe(takeUntil(this.destroy$)).subscribe(language => this.projectLanguage = language);
    this.sessioneService.fileName$.pipe(takeUntil(this.destroy$)).subscribe(filename => this.fileName = filename);
    this.sessioneService.sessionName$.pipe(takeUntil(this.destroy$)).subscribe(name => {
      this.currentSessionName = name || 'Untitled Session';
    });
    this.sessioneService.displayName$.pipe(takeUntil(this.destroy$)).subscribe(name => {
      this.currentSessionDisplayName = name || 'Untitled Session';
    });
    this.sessioneService.additionalContext$.pipe(takeUntil(this.destroy$)).subscribe(add_notes => this.additionalNotes = add_notes);
    this.sessioneService.selectedTemplate$.pipe(takeUntil(this.destroy$)).subscribe(template => this.selectedTemplate = template);
    this.sessioneService.templates$.pipe(takeUntil(this.destroy$)).subscribe(templates => {
      this.templates = templates;
      this.templateNames = this.templates.map(template => template.templateName);
      // Probabilmente anche questa riga non serve più se gestita altrove o se il template default è vuoto
      if (this.templateNames.length > 0 && !this.selectedTemplate) {
        this.sessioneService.updateSelectedTemplate(this.templateNames[0]);
      }
    });
    this.sessioneService.selectedTestStyle$.pipe(takeUntil(this.destroy$)).subscribe(testStyle => this.testStyle = testStyle);
    this.sessioneService.refinedStoryString$.pipe(takeUntil(this.destroy$)).subscribe(refinedStoryString => this.refinedStoryString = refinedStoryString);
    this.authService.ruolo$.pipe(takeUntil(this.destroy$)).subscribe(ruolo => this.ruolo = ruolo);
    this.sessioneService.isAgenticMode$.pipe(takeUntil(this.destroy$)).subscribe(isAgentic => this.isAgenticMode = isAgentic);
    this.sessioneService.topN$.pipe(takeUntil(this.destroy$)).subscribe(topN => this.topN = topN);

    // Sync chat context data
    this.sessioneService.requirementsAnalysis$.pipe(takeUntil(this.destroy$)).subscribe(reqAnalysis => this.requirementsAnalysis = reqAnalysis);
    this.sessioneService.macroScenarios$.pipe(takeUntil(this.destroy$)).subscribe(macros => this.macroScenariosUI = macros || []);
    this.sessioneService.selectedMicroScenario$.pipe(takeUntil(this.destroy$)).subscribe(micro => this.selectedMicroScenario = micro);
    this.sessioneService.microScenarioSpecifications$.pipe(takeUntil(this.destroy$)).subscribe(specs => this.microScenarioSpecifications = specs);
    // Note: selectedMicroScenario is currently managed within the workspace components,
    // but we prepare the property here for chat context integration.
  }

  // Helper methods to calculate differences
  getDifferenceTokens(current: number, previous: number): number {
    return current - previous;
  }

  getDifferenceCost(current: number, previous: number): number {
    return current - previous;
  }

  checkForSubtable(): void {
    if (this.selectedTemplate && this.templates) {
      const selectedTemplateObj = this.templates.find(template => template.templateName === this.selectedTemplate);
      if (selectedTemplateObj) {
        this.hasSubtable = this.templateHasSubtable(selectedTemplateObj);
      }
    }
  }

  templateHasSubtable(template: any): boolean {
    if (template && template.columns && Array.isArray(template.columns)) {
      return template.columns.some(column =>
        column.list && Array.isArray(column.subColumns) && column.subColumns.length > 0
      );
    }
    return false;
  }

  goBackToSessions(): void {
    this.router.navigate(['/projects/sessions', this.projectId]);
  }

  goToTests() {
    this.activeTab = 'testing';
  }

  openGeneraTestsModal() {
    this.modalService.dismissAll();
    this.modalService.open(InferenceSettingsModalComponent, { size: 'lg', scrollable: true });
  }

  handleGenerateTests(testStyle: string) {
    this.activeTab = 'testing';
    this.sessioneService.resetTests();
    setTimeout(() => {
      this.changeDetectorRef.detectChanges();

      if (this.testComponent) {
        this.testComponent.generateTests(testStyle);
      } else {
        console.error('TestComponent not found after tab change');
      }

      console.log('generando test');
    }, 0);
  }

  downloadGeneratedTests() {
    this.downloader.saveCurrentState(
      this.ruolo,
      this.fileName,
    );
  }

  // method to save the session on demand
  saveCurrentWorkspace(): void {
    this.spinnerService.show();
    this.sessionPersistenceService.saveSessionOnDemand() // Questa chiama saveCurrentSession internamente
      .pipe(
        take(1)
      )
      .subscribe({
        next: (response) => {
          this.spinnerService.hide();
          Swal.fire({
            title: 'Saved!',
            text: 'Workspace saved successfully',
            icon: 'success',
            timer: 2000,
            showConfirmButton: false,
            timerProgressBar: true
          });
        },
        error: (error) => {
          this.spinnerService.hide();
          console.error('Error saving workspace:', error);

          // --- MODIFICA CHIAVE ---
          // Usiamo il messaggio specifico dall'errore nel testo dell'alert.
          const errorMessage = error.message || 'Failed to save workspace. An unknown error occurred.';
          Swal.fire({
            title: 'Error',
            text: errorMessage, // Qui usiamo il messaggio dinamico
            icon: 'error'
            // Rimuoviamo il timer per dare all'utente il tempo di leggere l'errore
          });
        }
      });
  }

  // ========== SESSION NAME EDITING METHODS (FIXED) ==========
  startEditingSessionName(): void {
    this.isEditingSessionName = true;
    this.editingSessionName = this.currentSessionName;

    // Focus the input after the view updates
    setTimeout(() => {
      if (this.sessionNameInput) {
        this.sessionNameInput.nativeElement.focus();
        this.sessionNameInput.nativeElement.select();
      }
    }, 0);
  }

  saveSessionName(): void {
    if (!this.editingSessionName.trim()) {
      this.toastr.warning('Session name cannot be empty');
      return;
    }

    const newName = this.editingSessionName.trim();
    this.spinnerService.show();

    // --- CHANGE HERE: Use updateDisplayName ---
    this.sessionPersistenceService.updateDisplayName(newName)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.spinnerService.hide();
          this.isEditingSessionName = false;
          this.toastr.success('Session name updated successfully');
        },
        error: (error) => {
          this.spinnerService.hide();
          console.error('Error updating name:', error);
          this.toastr.error('Failed to update session name');
        }
      });
    // ------------------------------------------
  }

  cancelEditingSessionName(): void {
    this.isEditingSessionName = false;
    this.editingSessionName = '';
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
